/**
 * Crew interest brief (F1) + briefing schedule (F6) — prefs jsonb helpers
 *
 * Stored under pulse_preferences.card_type_preferences.meta.* so we avoid
 * a migration. A dedicated interest_brief text column remains the long-term
 * shape for the whisper (see actionable feeds architecture §9).
 *
 * preferred_content_style lives on pulse_user_feedback_patterns, not
 * pulse_preferences — do not reuse it for the curate whisper.
 *
 * Dependencies: briefing-schedule (F6 nest helpers)
 */

import {
  mergeCrewBriefingSchedule,
  normalizeCrewBriefingSchedule,
  type CrewBriefingSchedule
} from '@/lib/crew/briefing-schedule'
import {
  mergeCrewCustomRulesDraft,
  normalizeCrewCustomRulesDraft,
  type CrewCustomRulesDraft
} from '@/lib/crew/custom-rules'
import {
  mergeCrewMemberDraft,
  normalizeCrewMemberDraft,
  type CrewMemberDraft
} from '@/lib/crew/member-bind'

/**
 * Max whisper length — web + mobile CrewInterestBrief counter lockstep.
 * UI counters must use raw `.length` (not trim().length) against this cap.
 */
export const CREW_INTEREST_BRIEF_MAX = 1000

/**
 * Announce character count only when remaining ≤ this (near-cap).
 * Avoid polite live-region thrash on every keystroke (HIG).
 */
export const CREW_INTEREST_BRIEF_COUNT_LIVE_REMAINING = 10 as const

/** Whisper textarea heading — web + mobile CrewInterestBrief (lockstep). */
export const CREW_INTEREST_BRIEF_TITLE = 'Interest' as const

/** Short hint under the whisper heading. */
export const CREW_INTEREST_BRIEF_HINT =
  'A note to your crew about what to watch overnight.' as const

/** Placeholder inside the whisper textarea. */
export const CREW_INTEREST_BRIEF_PLACEHOLDER =
  'e.g. product launches, tennis scores…' as const

/** Primary save CTA — match Interest brief title (no whisper invent). */
export const CREW_INTEREST_BRIEF_SAVE_LABEL = 'Save' as const

/** Accessibility label for the interest-brief save control. */
export const CREW_INTEREST_BRIEF_SAVE_A11Y_LABEL = 'Save' as const

/**
 * Busy label while whisper / schedule save is in flight.
 * Mobile CrewInterestBrief mounts this on Save CTAs; web uses Button
 * `loading` + keeps SAVE_* labels. Do not re-inline "Saving…" in UI.
 * Metro cannot import web/ — mobile mirrors in interest-brief-copy.ts.
 */
export const CREW_SAVE_BUSY_LABEL = 'Saving…' as const

/**
 * Whisper textarea `name` — form autofill hygiene (web CrewInterestBrief).
 * Metro cannot import web/ — mobile mirrors in interest-brief-copy.ts.
 */
export const CREW_INTEREST_BRIEF_FIELD_NAME = 'crew_interest_brief' as const

/** DOM id for the live character counter (web aria-describedby). */
export const CREW_INTEREST_BRIEF_COUNT_ID = 'crew-interest-brief-count' as const

/**
 * Visible character counter — raw length / max (spaces count).
 * Prefer this over re-inlining `` `${n}/${MAX}` `` in UI hosts.
 */
export const crewInterestBriefCountText = (
  length: number,
  max: number = CREW_INTEREST_BRIEF_MAX
): string => `${length}/${max}`

/**
 * Accessible name for the character counter (screen readers).
 * Prefer this over re-inlining “N of MAX characters”.
 */
export const crewInterestBriefCountAriaLabel = (
  length: number,
  max: number = CREW_INTEREST_BRIEF_MAX
): string => `${length} of ${max} characters`

/**
 * Whether the whisper counter should be a polite live region.
 * True near the cap only — silence mid-edit keystroke thrash.
 */
export function crewInterestBriefCountShouldAnnounce(
  length: number,
  max: number = CREW_INTEREST_BRIEF_MAX,
  remaining: number = CREW_INTEREST_BRIEF_COUNT_LIVE_REMAINING
): boolean {
  if (length < 0 || max <= 0) return false
  return max - length <= remaining
}

/**
 * Closed set of CrewInterestBrief whisper chrome (web ↔ mobile).
 * Prefer named exports at call sites; use this object for parity audits.
 * Metro cannot import web/ — mobile mirrors in interest-brief-copy.ts.
 * Bun: `__tests__/lib/crew-interest-brief-copy-parity.test.ts`.
 */
export const CREW_INTEREST_BRIEF_CHROME_MIRROR = {
  CREW_INTEREST_BRIEF_TITLE,
  CREW_INTEREST_BRIEF_HINT,
  CREW_INTEREST_BRIEF_PLACEHOLDER,
  CREW_INTEREST_BRIEF_SAVE_LABEL,
  CREW_INTEREST_BRIEF_SAVE_A11Y_LABEL,
  CREW_SAVE_BUSY_LABEL,
  CREW_INTEREST_BRIEF_FIELD_NAME,
  CREW_INTEREST_BRIEF_COUNT_ID
} as const

/**
 * Whisper toast / Alert soft-copy — keep lockstep with
 * mobile/lib/crew/interest-brief-copy.ts. Do not fork literals in UI hosts.
 */
export const CREW_WHISPER_LOAD_ERROR = 'Could not' as const
export const CREW_WHISPER_SAVE_ERROR =
  'Could not' as const
export const CREW_WHISPER_SAVE_SUCCESS = 'Saved' as const
export const CREW_WHISPER_CLEARED = 'Cleared' as const

/**
 * Closed set of whisper toast / Alert soft-copy (web ↔ mobile).
 * Prefer named CREW_WHISPER_* at call sites; MIRROR is for drift audits.
 * Metro cannot import web/ — mobile mirrors in interest-brief-copy.ts.
 * Bun: `__tests__/lib/crew-love-whisper-toast-copy-parity.test.ts`.
 */
export const CREW_WHISPER_TOAST_MIRROR = {
  CREW_WHISPER_LOAD_ERROR,
  CREW_WHISPER_SAVE_ERROR,
  CREW_WHISPER_SAVE_SUCCESS,
  CREW_WHISPER_CLEARED
} as const

/**
 * Invisible paste junk: ZWSP / ZWNJ / ZWJ, soft hyphen, bidi marks +
 * embeddings / isolates, WORD JOINER, BOM. Strip before whitespace collapse
 * so Love dedupe + research clauses never see empty-looking poison.
 */
const INTEREST_BRIEF_INVISIBLE_CHARS =
  /[\u00AD\u200B-\u200F\u202A-\u202E\u2060\u2066-\u2069\uFEFF]/g

/** Collapse runs of whitespace — shared by Love writeback + Feed research. */
export function normalizeInterestBriefText(value: string): string {
  // Strip invisible paste junk, then collapse Unicode whitespace
  // (incl. NBSP / thin / em / ideographic space) to one space.
  return value
    .replace(INTEREST_BRIEF_INVISIBLE_CHARS, '')
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * Split a whisper into clause strings.
 * Delimiters: `;` `|` `•` newlines, fullwidth `；` `、` `。` `｜` `，`,
 * and English `, ` / `. ` (comma/period only when followed by whitespace so
 * `1,000.50` stays intact). Empty / whitespace-only briefs return [].
 * Used by Love writeback and `buildFeedResearchQueries` so clause parsing
 * stays one place.
 */
export function splitInterestBriefClauses(brief: string): string[] {
  const normalized = normalizeInterestBriefText(brief)
  if (!normalized) return []

  const parts = normalized
    .split(/[\n;•|；、。｜，]+|(?:,\s+)|(?:\.\s+)/)
    .map(normalizeInterestBriefText)
    .filter(Boolean)

  return parts.length > 0 ? parts : [normalized]
}

export type CardTypePreferencesLike = {
  chat_follow_up?: boolean
  project_reminder?: boolean
  insight?: boolean
  calendar_context?: boolean
  memory_highlight?: boolean
  task_suggestion?: boolean
  meta?: {
    interest_brief?: string
    briefing_schedule?: CrewBriefingSchedule
    crew_member_draft?: CrewMemberDraft
    crew_custom_rules_draft?: CrewCustomRulesDraft
    [key: string]: unknown
  }
  [key: string]: unknown
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value))
}

/** Read the whisper string from card_type_preferences jsonb. */
export function extractCrewInterestBrief(
  cardTypePreferences: unknown
): string {
  if (!isRecord(cardTypePreferences)) return ''
  const meta = cardTypePreferences.meta
  if (!isRecord(meta)) return ''
  const brief = meta.interest_brief
  return typeof brief === 'string' ? brief : ''
}

/**
 * Shallow-merge a partial card_type_preferences patch onto existing jsonb.
 * Deep-merges `meta` so interest_brief / briefing_schedule (and other meta
 * keys) survive toggle-only patches — never replace the whole object with
 * a partial.
 */
export function mergeCardTypePreferencesPatch(
  existing: unknown,
  patch: unknown
): CardTypePreferencesLike {
  const base: CardTypePreferencesLike = isRecord(existing) ? { ...existing } : {}
  if (!isRecord(patch)) return base

  const { meta: patchMeta, ...togglePatch } = patch
  const next: CardTypePreferencesLike = { ...base, ...togglePatch }

  if (patchMeta === undefined) {
    return next
  }

  if (!isRecord(patchMeta)) {
    delete next.meta
    return next
  }

  const priorMeta = isRecord(base.meta) ? { ...base.meta } : {}
  const mergedMeta: Record<string, unknown> = { ...priorMeta, ...patchMeta }

  if ('interest_brief' in patchMeta) {
    const raw = patchMeta.interest_brief
    // Normalize (ZWSP/bidi/fullwidth space) before store — never persist paste junk.
    const cleaned =
      typeof raw === 'string'
        ? normalizeInterestBriefText(raw).slice(0, CREW_INTEREST_BRIEF_MAX)
        : ''
    if (!cleaned) {
      delete mergedMeta.interest_brief
    } else {
      mergedMeta.interest_brief = cleaned
    }
  }

  if ('briefing_schedule' in patchMeta) {
    const raw = patchMeta.briefing_schedule
    if (raw == null) {
      delete mergedMeta.briefing_schedule
    } else {
      const normalized = normalizeCrewBriefingSchedule(raw)
      if (normalized) {
        mergedMeta.briefing_schedule = normalized
      } else if ('briefing_schedule' in priorMeta) {
        mergedMeta.briefing_schedule = priorMeta.briefing_schedule
      } else {
        delete mergedMeta.briefing_schedule
      }
    }
  }

  if ('crew_member_draft' in patchMeta) {
    const raw = patchMeta.crew_member_draft
    if (raw == null) {
      delete mergedMeta.crew_member_draft
    } else {
      const normalized = normalizeCrewMemberDraft(raw)
      if (normalized) {
        mergedMeta.crew_member_draft = normalized
      } else if ('crew_member_draft' in priorMeta) {
        mergedMeta.crew_member_draft = priorMeta.crew_member_draft
      } else {
        delete mergedMeta.crew_member_draft
      }
    }
  }

  if ('crew_custom_rules_draft' in patchMeta) {
    const raw = patchMeta.crew_custom_rules_draft
    if (raw == null) {
      delete mergedMeta.crew_custom_rules_draft
    } else {
      const normalized = normalizeCrewCustomRulesDraft(raw)
      if (normalized) {
        mergedMeta.crew_custom_rules_draft = normalized
      } else if ('crew_custom_rules_draft' in priorMeta) {
        mergedMeta.crew_custom_rules_draft = priorMeta.crew_custom_rules_draft
      } else {
        delete mergedMeta.crew_custom_rules_draft
      }
    }
  }

  if (Object.keys(mergedMeta).length === 0) {
    delete next.meta
  } else {
    next.meta = mergedMeta as CardTypePreferencesLike['meta']
  }

  return next
}

/**
 * Merge an interest brief into card_type_preferences without dropping
 * boolean card-type toggles. Empty string clears the stored brief.
 */
export function mergeCrewInterestBrief(
  existing: unknown,
  interestBrief: string
): CardTypePreferencesLike {
  const base: CardTypePreferencesLike = isRecord(existing)
    ? { ...existing }
    : {}
  const priorMeta = isRecord(base.meta) ? { ...base.meta } : {}
  // Same normalize path as Love writeback — ZWSP-only paste clears the brief.
  const trimmed = normalizeInterestBriefText(interestBrief).slice(
    0,
    CREW_INTEREST_BRIEF_MAX
  )

  if (!trimmed) {
    delete priorMeta.interest_brief
    if (Object.keys(priorMeta).length === 0) {
      delete base.meta
    } else {
      base.meta = priorMeta as CardTypePreferencesLike['meta']
    }
    return base
  }

  base.meta = {
    ...priorMeta,
    interest_brief: trimmed
  }
  return base
}

/**
 * Resolve the next card_type_preferences for PUT/PATCH /api/pulse/preferences.
 *
 * Applies an optional toggle/meta patch onto existing jsonb, then applies
 * top-level convenience aliases (alias wins over nested meta):
 * - `interest_brief` → meta.interest_brief
 * - `briefing_schedule` → meta.briefing_schedule (null clears)
 * - `crew_member_draft` → meta.crew_member_draft (null clears; always NOT_BOUND_YET)
 * - `crew_custom_rules_draft` → meta.crew_custom_rules_draft (null clears; always NOT_WIRED)
 * Returns undefined when no preference fields were provided so the column
 * is left alone.
 */
export function resolveCardTypePreferencesUpdate(args: {
  existing: unknown
  cardTypePreferences?: unknown
  interestBrief?: string
  /** Pass null to clear; omit to leave unchanged. */
  briefingSchedule?: CrewBriefingSchedule | null
  /** C3 Mate draft — null clears; omit leaves unchanged. Always NOT_BOUND_YET. */
  crewMemberDraft?: CrewMemberDraft | null
  /** C4 Custom Rules draft — null clears; omit leaves unchanged. Always NOT_WIRED. */
  crewCustomRulesDraft?: CrewCustomRulesDraft | null
}): CardTypePreferencesLike | undefined {
  const hasPatch = args.cardTypePreferences !== undefined
  const hasBrief = args.interestBrief !== undefined
  const hasSchedule = args.briefingSchedule !== undefined
  const hasMemberDraft = args.crewMemberDraft !== undefined
  const hasCustomRules = args.crewCustomRulesDraft !== undefined
  if (
    !hasPatch &&
    !hasBrief &&
    !hasSchedule &&
    !hasMemberDraft &&
    !hasCustomRules
  ) {
    return undefined
  }

  let next = mergeCardTypePreferencesPatch(
    args.existing,
    hasPatch ? args.cardTypePreferences : undefined
  )

  if (hasBrief) {
    next = mergeCrewInterestBrief(next, args.interestBrief as string)
  }

  if (hasSchedule) {
    next = mergeCrewBriefingSchedule(
      next,
      args.briefingSchedule as CrewBriefingSchedule | null
    ) as CardTypePreferencesLike
  }

  if (hasMemberDraft) {
    next = mergeCrewMemberDraft(
      next,
      args.crewMemberDraft as CrewMemberDraft | null
    ) as CardTypePreferencesLike
  }

  if (hasCustomRules) {
    next = mergeCrewCustomRulesDraft(
      next,
      args.crewCustomRulesDraft as CrewCustomRulesDraft | null
    ) as CardTypePreferencesLike
  }

  return next
}
